import { object } from 'prop-types';
import authAxios from 'src/utils/authAxios';

type ResponseObj = {
  status: number;
  message: string;
  data?: any[];
};

type userListObj = {
  status: number;
  message: string;
  data: {
    users: any[];
    usedSeats: number;
    totalSeats: number;
    holdsSeat: boolean;
  };
};

/**
 * @param userId
 * @param values
 * @returns
 */
export const requestCollaborator = async (
  companyId: string,
  email: string,
  role: string
): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(`shared-user/invite-user/${companyId}`, {
      email: email,
      role: role,
    });
    return res.data;
  } catch (err) {
    return { status: 404, message: "Couldn't invite user" };
  }
};

export const requestCollaboratorWithoutAccount = async (
  companyId: string,
  email: string,
  role: string
): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(
      `shared-user/invite-new-user/${companyId}`,
      {
        email: email,
        role: role,
      }
    );
    return res.data;
  } catch (err) {
    return { status: 404, message: "Couldn't invite user" };
  }
};

export const acceptInvite = async (
  userCompanyId: string,
  inviteCompanyId: string
): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(
      `shared-user/accept-invitation/${userCompanyId}`,
      {
        companyId: inviteCompanyId,
      }
    );
    return res.data;
  } catch (err) {
    return { status: 404, message: 'No valid invitation found' };
  }
};

export const getUsersInCompany = async (
  companyId: string
): Promise<userListObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.get(
      `shared-user/get-users-in-company/${companyId}/`
    );
    return res.data;
  } catch (err) {
    return {
      status: 400,
      message: "Couldn't get users in company",
      data: {
        users: [],
        usedSeats: 0,
        totalSeats: 0,
        holdsSeat: false,
      },
    };
  }
};

/**
 *
 * @param userId The user id of the user to update
 * @param companyId The company id of the logged in user (the company that the user is being updated in)
 * @param role The role to update the user to
 * @param hasAccess If the user should have access to the company, if false the user will be removed from the company
 * @param expirationDate The access expiration date, The user will no longer have access to the company after this date
 *
 */
export const updateUserRole = async (
  userId: string,
  companyId: string,
  role: string,
  hasAccess: boolean,
  expirationDate: string
): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(
      `shared-user/update-user-in-company/${companyId}`,
      {
        companyId: companyId,
        userId: userId,
        role: role,
        hasAccess: hasAccess,
        expirationDate: expirationDate,
      }
    );
    return res.data;
  } catch (err) {
    return { status: 400, message: "Couldn't update user role in company" };
  }
};

export const getUserBusinesses = async (): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.get(`shared-user/get-user-companies/`, {
      timeout: 15000,
    });
    return res.data;
  } catch (err) {
    return { status: 400, message: "Couldn't get user businesses", data: null };
  }
};

export const removeSelfFromBusiness = async (
  companyId: string
): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(
      `shared-user/remove-self-from-company/${companyId}`,
      {}
    );
    return res.data;
  } catch (err) {
    return {
      status: 400,
      message: "Couldn't remove yourself from the business",
    };
  }
};

export const purchaseSeat = async (companyId: string): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(`shared-user/purchase-seats/${companyId}`);
    return res.data;
  } catch (err) {
    return { status: 400, message: "Couldn't purchase seats" };
  }
};

export const removeSeat = async (companyId: string): Promise<ResponseObj> => {
  try {
    const axiosReq = await authAxios();
    const res = await axiosReq.post(`shared-user/remove-seat/${companyId}`);
    return res.data;
  } catch (err) {
    return { status: 400, message: "Couldn't remove seat" };
  }
};
